// PRIORITIES-HOME: the pure side of Cadence › Priorities — a priority's status (its track while
// open, else its state), the groups the page draws (the company's seats at the top of the chart
// first, then every seat in chart order; or by team), the quarter's summary, and quarter keys.
// The track vocabulary is the chart's (features/chart/keystones.ts), so the page, the Keystones
// lens and the seat panel say the same words.
import type { LensBand } from "@companyos/domain-work/org";
import type { Goal } from "../../api/types";
import type { OrgChart } from "../chart/chart-model";
import { seatHolderLabel } from "../format";
import { type Holder, holdersOf } from "../home/home-queries";

export type Status = "on" | "at_risk" | "off" | "done" | "missed" | "archived";

/** What the status control offers: the three tracks of an open priority, and done. */
export const STATUS_CHOICES: readonly Status[] = ["on", "at_risk", "off", "done"];

export const STATUS_LABELS: Record<Status, string> = {
  on: "On track",
  at_risk: "At risk",
  off: "Off track",
  done: "Done",
  missed: "Missed",
  archived: "Archived",
};

/** The dot's band (never colour alone: the label is beside it or names it). */
export const STATUS_BAND: Record<Status, LensBand | "none"> = {
  on: "green",
  at_risk: "amber",
  off: "red",
  done: "green",
  missed: "none",
  archived: "none",
};

/** An open priority reads as its track; achieved is done, missed is missed, dropped archived. */
export function statusOf(g: Pick<Goal, "state" | "track">): Status {
  if (g.state === "achieved") return "done";
  if (g.state === "missed") return "missed";
  if (g.state === "dropped") return "archived";
  return g.track;
}

/** The patch that puts a priority in a status (done = achieved; a track reopens it). */
export function patchFor(status: Status): Partial<Pick<Goal, "state" | "track">> {
  if (status === "done") return { state: "achieved" };
  if (status === "missed") return { state: "missed" };
  if (status === "archived") return { state: "dropped" };
  return { state: "open", track: status };
}

export const milestonesDone = (g: Pick<Goal, "milestones">) =>
  g.milestones.filter((m) => m.done_at !== null).length;

export type Summary = Record<Status, number> & {
  total: number;
  milestones: { done: number; total: number };
};

/** The quarter's counts over the priorities shown (archived ones are counted apart). */
export function summarise(goals: readonly Goal[]): Summary {
  const out: Summary = {
    on: 0,
    at_risk: 0,
    off: 0,
    done: 0,
    missed: 0,
    archived: 0,
    total: 0,
    milestones: { done: 0, total: 0 },
  };
  for (const g of goals) {
    const s = statusOf(g);
    out[s] += 1;
    if (s === "archived") continue;
    out.total += 1;
    out.milestones.done += milestonesDone(g);
    out.milestones.total += g.milestones.length;
  }
  return out;
}

export type SeatInfo = {
  id: string;
  title: string;
  holders: Holder[];
  /** At the top of the chart (no supervisor): its priorities are the company's. */
  top: boolean;
};

export type Group = {
  key: string;
  kind: "company" | "seat" | "team" | "no-team";
  label: string;
  /** The seat of a seat or company group (its holder is the owner). */
  seat?: SeatInfo;
  goals: Goal[];
};

/** The chart's seats in tree order (each seat before its reports, siblings by title). */
export function seatsInOrder(chart: OrgChart | undefined): SeatInfo[] {
  if (!chart) return [];
  const holders = holdersOf(chart);
  const seats = chart.seats.filter((s) => s.status !== "proposed");
  const ids = new Set(seats.map((s) => s.id));
  const kids = new Map<string | null, typeof seats>();
  for (const s of seats) {
    const parent =
      s.supervisor_seat_id && ids.has(s.supervisor_seat_id) ? s.supervisor_seat_id : null;
    kids.set(parent, [...(kids.get(parent) ?? []), s]);
  }
  const out: SeatInfo[] = [];
  const seen = new Set<string>();
  const walk = (parent: string | null) => {
    for (const s of [...(kids.get(parent) ?? [])].sort((a, b) => a.title.localeCompare(b.title))) {
      if (seen.has(s.id)) continue;
      seen.add(s.id);
      out.push({
        id: s.id,
        title: s.title,
        holders: holders.get(s.id) ?? [],
        top: parent === null,
      });
      walk(s.id);
    }
  };
  walk(null);
  return out;
}

const byCreated = (a: Goal, b: Goal) =>
  a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id);

/**
 * By owner: the company first (the seats at the top of the chart — the company's own
 * priorities), then every seat with a priority in chart order. `withEmpty` keeps the seats the
 * caller may write that have none yet, so "Add" has a row to land in.
 */
export function groupBySeat(
  goals: readonly Goal[],
  seats: readonly SeatInfo[],
  withEmpty: ReadonlySet<string> = new Set(),
): Group[] {
  const bySeat = new Map<string, Goal[]>();
  for (const g of goals) bySeat.set(g.seat_id, [...(bySeat.get(g.seat_id) ?? []), g]);
  const out: Group[] = [];
  for (const s of seats) {
    const list = (bySeat.get(s.id) ?? []).sort(byCreated);
    bySeat.delete(s.id);
    if (!list.length && !withEmpty.has(s.id)) continue;
    out.push({
      key: s.id,
      kind: s.top ? "company" : "seat",
      label: s.top ? "Company" : seatHolderLabel(s.title, s.holders[0]?.name),
      seat: s,
      goals: list,
    });
  }
  // A priority on a seat the chart no longer draws (proposed, or not yet loaded) still shows.
  for (const [seatId, list] of bySeat)
    out.push({ key: seatId, kind: "seat", label: "A seat", goals: list.sort(byCreated) });
  return out;
}

/** By team: each team of the chart with its seats' priorities, then "No team". */
export function groupByTeam(
  goals: readonly Goal[],
  chart: OrgChart | undefined,
  seats: readonly SeatInfo[],
): Group[] {
  const order = new Map(seats.map((s, i) => [s.id, i]));
  const sorted = [...goals].sort(
    (a, b) => (order.get(a.seat_id) ?? 1e9) - (order.get(b.seat_id) ?? 1e9) || byCreated(a, b),
  );
  const teamsOfSeat = new Map<string, Set<string>>();
  for (const m of chart?.team_members ?? [])
    teamsOfSeat.set(m.seat_id, (teamsOfSeat.get(m.seat_id) ?? new Set()).add(m.team_id));
  const out: Group[] = [];
  for (const t of [...(chart?.teams ?? [])].sort((a, b) => a.name.localeCompare(b.name))) {
    const list = sorted.filter((g) =>
      g.team_id ? g.team_id === t.id : teamsOfSeat.get(g.seat_id)?.has(t.id),
    );
    if (list.length) out.push({ key: t.id, kind: "team", label: t.name, goals: list });
  }
  const none = sorted.filter((g) => !g.team_id && !teamsOfSeat.get(g.seat_id)?.size);
  if (none.length) out.push({ key: "no-team", kind: "no-team", label: "No team", goals: none });
  return out;
}

const QUARTER = /^(\d{4})-Q([1-4])$/;

/** "Q4 2026" for `2026-Q4`. */
export function quarterLabel(key: string): string {
  const m = QUARTER.exec(key);
  return m ? `Q${m[2]} ${m[1]}` : key;
}

/** The quarter `n` quarters after (or before, negative) a quarter key. */
export function shiftQuarter(key: string, n: number): string {
  const m = QUARTER.exec(key);
  if (!m) return key;
  const i = Number(m[1]) * 4 + Number(m[2]) - 1 + n;
  return `${Math.floor(i / 4)}-Q${(i % 4) + 1}`;
}

/** A due date as the row shows it: "Dec 31" (with the year when it is not `year`). */
export function dueLabel(iso: string | null, year: number): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", timeZone: "UTC" };
  if (d.getUTCFullYear() !== year) opts.year = "numeric";
  return d.toLocaleDateString("en-US", opts);
}
